/**
 * Admin Avatar picker (docs/avatar-integration-spec.md §2.4): choose a family
 * preset, an Avatar you can EDIT, or "Default", with a static preview. The
 * library is not every Avatar in the org: binding one needs edit rights on it
 * (§12 "Your avatars + bind authorization"), for admins too. Replaces the free-text Avatar URL; the choice is
 * written as the template's `avatar` binding by the caller, never avatar_url.
 *
 * `choice`: undefined = leave as is, null = reset to the org default,
 * a string = bind that Avatar artifact.
 */
import { useEffect, useState } from 'react'
import { AgentAvatar } from '@ponder/artifacts/agent-avatar'
import { adminApi, type AdminAvatarOption, type AdminAvatarRef } from '@/lib/api'

export type AvatarChoice = string | null | undefined

interface AvatarPickerProps {
  /** What the agent shows now (with its static render), when editing an existing agent. */
  current?: AdminAvatarRef | null
  /** True when `current` is the template's own binding (Default is then a change). */
  currentIsOwn?: boolean
  choice: AvatarChoice
  onChoose: (choice: AvatarChoice) => void
  disabled?: boolean
}

function optionRef(o: AdminAvatarOption): AdminAvatarRef {
  return { artifactId: o.artifactId, version: o.version, ...(o.svg ? { svg: o.svg } : {}) }
}

function label(o: AdminAvatarOption): string {
  if (o.family) return o.family
  return o.name.replace(/\s*\((family|default agent) avatar\)\s*$/i, '').trim() || 'Avatar'
}

export function AvatarPicker({ current, currentIsOwn, choice, onChoose, disabled }: AvatarPickerProps) {
  const [options, setOptions] = useState<AdminAvatarOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    adminApi
      .getAvatarLibrary()
      .then((list) => alive && setOptions(list))
      .catch((e) => alive && setError(e?.response?.data?.error || 'Could not load avatars'))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [])

  const defaultOption = options.find((o) => o.isDefault) ?? null
  const chosenOption = typeof choice === 'string' ? options.find((o) => o.artifactId === choice) ?? null : null
  const previewRef: AdminAvatarRef | null =
    choice === undefined ? current ?? null : choice === null ? (defaultOption ? optionRef(defaultOption) : null) : chosenOption ? optionRef(chosenOption) : null

  // Which tile reads as selected: the explicit choice, else what it shows now.
  const selectedId = choice === undefined ? (currentIsOwn ? current?.artifactId ?? null : null) : choice
  const isDefaultSelected = selectedId === null

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <AgentAvatar agent={previewRef} size={56} className="text-foreground" label="Avatar preview" />
        <div className="text-sm text-muted-foreground">
          {choice === undefined
            ? currentIsOwn
              ? 'Its own avatar'
              : 'Shows the default avatar'
            : choice === null
              ? 'Will show the default avatar'
              : `Will show ${chosenOption ? label(chosenOption) : 'the chosen avatar'}`}
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div role="radiogroup" aria-label="Avatar" className="grid max-h-56 grid-cols-4 gap-2 overflow-y-auto sm:grid-cols-6">
        <button
          type="button"
          role="radio"
          aria-checked={isDefaultSelected}
          disabled={disabled || loading}
          onClick={() => onChoose(currentIsOwn ? null : undefined)}
          className={`flex flex-col items-center gap-1 rounded-md p-1 text-xs hover:bg-accent disabled:opacity-50 ${isDefaultSelected ? 'bg-accent' : ''}`}
        >
          <AgentAvatar agent={defaultOption ? optionRef(defaultOption) : null} size={40} decorative className="text-foreground" />
          <span className="max-w-full truncate text-muted-foreground">Default</span>
        </button>
        {loading
          ? Array.from({ length: 5 }, (_, i) => <div key={i} className="mx-auto h-10 w-10 animate-pulse rounded-full bg-muted" />)
          : options.map((o) => {
              const selected = o.artifactId === selectedId
              return (
                <button
                  key={o.artifactId}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={disabled}
                  title={o.name}
                  onClick={() => onChoose(o.artifactId)}
                  className={`flex flex-col items-center gap-1 rounded-md p-1 text-xs hover:bg-accent disabled:opacity-50 ${selected ? 'bg-accent' : ''}`}
                >
                  <AgentAvatar agent={optionRef(o)} size={40} decorative className="text-foreground" />
                  <span className="max-w-full truncate text-muted-foreground">{label(o)}</span>
                </button>
              )
            })}
      </div>
    </div>
  )
}

/** Write a picker choice as the template's avatar binding (undefined = no change). */
export async function applyAvatarChoice(agentId: string, choice: AvatarChoice): Promise<void> {
  if (choice === undefined) return
  if (choice === null) await adminApi.resetAgentAvatar(agentId)
  else await adminApi.setAgentAvatar(agentId, choice)
}
